import React, { useEffect, useState } from "react";
import { api } from "../../services/api";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Typography,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    useTheme,
    Paper,
    MenuItem,
    Snackbar,
    Alert,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import {
    ArrowDropDown,
    ArrowRight,
    Add,
} from "@mui/icons-material";

export interface Category {
    id: string;
    name: string;
    budgeted: number;
    actual: number;
    kind: "income" | "expense";
    average3Months?: number; // ← new optional field
    billingIntervalMonths?: number; // e.g. 1 = monthly, 6 = twice a year
    sortOrder?: number;
    subcategories?: Category[];
}

interface Props {
    categories: Category[];
    onChange: (updated: Category[], meta?: { change: "budget" | "structure" }) => void;
    onCreateCategory?: (input: {
        name: string;
        parentId: string | null;
        kind: "income" | "expense";
        billingIntervalMonths: number;
        sortOrder: number;
    }) => Promise<{ id: string; name: string; kind: "income" | "expense"; billingIntervalMonths: number; sortOrder: number }>;
    onUpdateCategory?: (id: string, patch: {
        name?: string;
        parentId?: string | null;
        kind?: "income" | "expense";
        billingIntervalMonths?: number;
        sortOrder?: number;
    }) => Promise<void>;
    onDeleteCategory?: (id: string) => Promise<void>;
}

export default function BudgetTable({ categories, onChange, onCreateCategory, onUpdateCategory, onDeleteCategory }: Props) {
    const theme = useTheme();
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [parentId, setParentId] = useState<string | null>(null);
    const [newName, setNewName] = useState("");
    const [newKind, setNewKind] = useState<"income" | "expense">("expense");
    const [newIntervalValue, setNewIntervalValue] = useState<number>(1);
    const [newIntervalUnit, setNewIntervalUnit] = useState<"months" | "years">("months");
    const [editTargetId, setEditTargetId] = useState<string | null>(null);
    const [editIntervalValue, setEditIntervalValue] = useState<number>(1);
    const [editIntervalUnit, setEditIntervalUnit] = useState<"months" | "years">("months");
    const [editKind, setEditKind] = useState<"income" | "expense">("expense");
    const [creatingCategory, setCreatingCategory] = useState(false);
    const [createCategoryError, setCreateCategoryError] = useState<string | null>(null);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteConfirmTargetId, setDeleteConfirmTargetId] = useState<string | null>(null);
    const [usageCounts, setUsageCounts] = useState<{ transactions: number; rules: number; allocations: number; childCategories: number } | null>(null);
    const [optUnassignTransactions, setOptUnassignTransactions] = useState(true);
    const [optDeleteRules, setOptDeleteRules] = useState(true);
    const [optDeleteAllocations, setOptDeleteAllocations] = useState(true);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "info" | "warning" | "error" }>(
        { open: false, message: "", severity: "error" }
    );

    // Expand all by default
    useEffect(() => {
        const allExpanded: Record<string, boolean> = {};
        const collect = (cats: Category[]) => {
            cats.forEach((c) => {
                allExpanded[c.id] = true;
                if (c.subcategories) collect(c.subcategories);
            });
        };
        collect(categories);
        setExpanded(allExpanded);
    }, [categories]);

    const toggleExpand = (id: string) =>
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

    const computeSums = (cat: Category): { budgeted: number; actual: number; average3Months: number } => {
        if (!cat.subcategories?.length) {
            return {
                budgeted: cat.budgeted,
                actual: cat.actual,
                average3Months: cat.average3Months ?? 0,
            };
        }
        return cat.subcategories.reduce(
            (acc, sub) => {
                const subTotals = computeSums(sub);
                acc.budgeted += subTotals.budgeted;
                acc.actual += subTotals.actual;
                acc.average3Months += subTotals.average3Months;
                return acc;
            },
            { budgeted: 0, actual: 0, average3Months: 0 }
        );
    };

    const handleBudgetChange = (id: string, value: number, cats = categories): Category[] =>
        cats.map((c) => {
            if (c.id === id) return { ...c, budgeted: value };
            if (c.subcategories)
                return { ...c, subcategories: handleBudgetChange(id, value, c.subcategories) };
            return c;
        });

    const getSiblings = (cats: Category[], parentId: string | null): Category[] => {
        if (!parentId) return cats;
        const parent = findCategoryById(cats, parentId);
        return parent?.subcategories ?? [];
    };

    const getNextSortOrder = (cats: Category[], parentId: string | null): number => {
        const siblings = getSiblings(cats, parentId);
        const max = siblings.reduce((acc, s) => Math.max(acc, s.sortOrder ?? 0), 0);
        return siblings.length ? max + 10 : 0;
    };

    const findCategoryById = (cats: Category[], id: string): Category | undefined => {
        for (const c of cats) {
            if (c.id === id) return c;
            if (c.subcategories) {
                const found = findCategoryById(c.subcategories, id);
                if (found) return found;
            }
        }
        return undefined;
    };

    const handleAddClick = (parentId: string | null = null) => {
        setParentId(parentId);
        setNewName("");
        setCreateCategoryError(null);
        if (parentId) {
            const parent = findCategoryById(categories, parentId);
            setNewKind(parent?.kind ?? "expense");
            const parentIntervalMonths = parent?.billingIntervalMonths ?? 1;
            if (parentIntervalMonths % 12 === 0) {
                setNewIntervalUnit("years");
                setNewIntervalValue(parentIntervalMonths / 12);
            } else {
                setNewIntervalUnit("months");
                setNewIntervalValue(parentIntervalMonths);
            }
        } else {
            setNewKind("expense");
            setNewIntervalUnit("months");
            setNewIntervalValue(1);
        }
        setDialogOpen(true);
    };

    const handleAddConfirm = async () => {
        if (!newName.trim()) return;
        setCreatingCategory(true);
        setCreateCategoryError(null);
        try {
            const value = Number.isFinite(newIntervalValue) && newIntervalValue > 0 ? newIntervalValue : 1;
            const intervalMonths =
                newIntervalUnit === "years" ? value * 12 : value;
            const sortOrder = getNextSortOrder(categories, parentId);

            const created = onCreateCategory
                ? await onCreateCategory({
                    name: newName.trim(),
                    parentId,
                    kind: newKind,
                    billingIntervalMonths: intervalMonths,
                    sortOrder,
                })
                : {
                    id: crypto.randomUUID(),
                    name: newName.trim(),
                    kind: newKind,
                    billingIntervalMonths: intervalMonths,
                    sortOrder,
                };

            const newCategory: Category = {
                id: created.id,
                name: created.name,
                budgeted: 0,
                actual: 0,
                kind: created.kind,
                average3Months: 0,
                billingIntervalMonths: created.billingIntervalMonths,
                sortOrder: created.sortOrder,
                subcategories: [],
            };

        const addRecursive = (cats: Category[]): Category[] =>
            cats.map((c) => {
                if (c.id === parentId) {
                    const updatedSubs = [...(c.subcategories || []), newCategory];
                    return { ...c, subcategories: updatedSubs };
                }
                if (c.subcategories)
                    return { ...c, subcategories: addRecursive(c.subcategories) };
                return c;
            });

            const updated = parentId
                ? addRecursive(categories)
                : [...categories, newCategory];

            onChange(updated, { change: "structure" });
            setDialogOpen(false);
        } catch (e) {
            setCreateCategoryError(e instanceof Error ? e.message : "Failed to create category");
        } finally {
            setCreatingCategory(false);
        }
    };

    const deleteCategoryById = (id: string, cats: Category[] = categories): Category[] =>
        cats
            .filter((c) => c.id !== id)
            .map((c) =>
                c.subcategories
                    ? { ...c, subcategories: deleteCategoryById(id, c.subcategories) }
                    : c
            );

    const handleOpenEdit = (cat: Category) => {
        setEditTargetId(cat.id);
        setEditKind(cat.kind);
        const intervalMonths = cat.billingIntervalMonths && cat.billingIntervalMonths > 0
            ? cat.billingIntervalMonths
            : 1;
        if (intervalMonths % 12 === 0) {
            setEditIntervalUnit("years");
            setEditIntervalValue(intervalMonths / 12);
        } else {
            setEditIntervalUnit("months");
            setEditIntervalValue(intervalMonths);
        }
    };

    const handleEditConfirm = async () => {
        if (!editTargetId) return;

        const value = Number.isFinite(editIntervalValue) && editIntervalValue > 0
            ? editIntervalValue
            : 1;
        const intervalMonths =
            editIntervalUnit === "years" ? value * 12 : value;

        try {
            if (onUpdateCategory) {
                await onUpdateCategory(editTargetId, {
                    kind: editKind,
                    billingIntervalMonths: intervalMonths,
                });
            }
        } catch (e) {
            setSnackbar({
                open: true,
                message: e instanceof Error ? e.message : "Failed to update category",
                severity: "error",
            });
            return;
        }

        const updateCategory = (cats: Category[]): Category[] =>
            cats.map((c) => {
                if (c.id === editTargetId) {
                    const propagateKind = (subs: Category[]): Category[] =>
                        subs.map((s) => ({
                            ...s,
                            kind: editKind,
                            subcategories: s.subcategories ? propagateKind(s.subcategories) : undefined,
                        }));
                    return { 
                        ...c, 
                        billingIntervalMonths: intervalMonths,
                        kind: editKind,
                        subcategories: c.subcategories ? propagateKind(c.subcategories) : undefined,
                    };
                }
                if (c.subcategories) {
                    return { ...c, subcategories: updateCategory(c.subcategories) };
                }
                return c;
            });

        onChange(updateCategory(categories), { change: "structure" });
        setEditTargetId(null);
    };

    const handleEditDelete = async () => {
        if (!editTargetId) return;
        setDeleteConfirmTargetId(editTargetId);
        setUsageCounts(null);
        setDeleteConfirmOpen(true);

        try {
            const usage = await api.previewCategoryUsage(editTargetId);
            setUsageCounts(usage);
            setOptUnassignTransactions(usage.transactions > 0);
            setOptDeleteRules(usage.rules > 0);
            setOptDeleteAllocations(usage.allocations > 0);
        } catch (e) {
            setSnackbar({ open: true, message: e instanceof Error ? e.message : "Failed to fetch usage", severity: "error" });
            // still open the dialog with no details
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmTargetId) return;
        try {
            await api.forceDeleteCategory(deleteConfirmTargetId, {
                unassignTransactions: optUnassignTransactions,
                deleteRules: optDeleteRules,
                deleteAllocations: optDeleteAllocations,
            });
        } catch (e) {
            setSnackbar({ open: true, message: e instanceof Error ? e.message : "Failed to delete category", severity: "error" });
            return;
        }

        const updated = deleteCategoryById(deleteConfirmTargetId);
        onChange(updated, { change: "structure" });
        setDeleteConfirmOpen(false);
        setDeleteConfirmTargetId(null);
        setEditTargetId(null);
        setSnackbar({ open: true, message: "Category deleted", severity: "success" });
    };

    // --- Render recursive rows ---
    const renderRows = (cats: Category[], depth = 0) =>
        cats.map((c) => {
            const hasSubs = !!c.subcategories?.length;
            const isExpanded = expanded[c.id];
            const totals = hasSubs
                ? computeSums(c)
                : {
                    budgeted: c.budgeted,
                    actual: c.actual,
                    average3Months: c.average3Months ?? 0,
                };

            return (
                <React.Fragment key={c.id}>
                    <TableRow
                        hover
                        onClick={() => handleOpenEdit(c)}
                        sx={{
                            cursor: "pointer",
                            backgroundColor:
                                depth === 0 ? theme.palette.action.hover : "inherit",
                            borderTop:
                                depth === 0 ? `2px solid ${theme.palette.divider}` : undefined,
                        }}
                    >
                        {/* Category cell */}
                        <TableCell>
                            <Box sx={{ pl: depth * 6, display: "flex", alignItems: "center" }}>
                                {hasSubs && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpand(c.id);
                                        }}
                                    >
                                        {isExpanded ? <ArrowDropDown /> : <ArrowRight />}
                                    </IconButton>
                                )}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {depth === 0 && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                                color: c.kind === "income" ? "success.main" : "error.main",
                                            }}
                                        >
                                            {c.kind === "income" ? "+" : "-"}
                                        </Typography>
                                    )}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "baseline",
                                            gap: 0.75,
                                        }}
                                    >
                                        <Typography
                                            variant={depth === 0 ? "subtitle1" : "body2"}
                                            sx={{
                                                fontWeight: depth === 0 ? 600 : 400,
                                            }}
                                        >
                                            {c.name}
                                        </Typography>
                                        {!hasSubs && c.billingIntervalMonths && c.billingIntervalMonths > 1 && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: 160,
                                                }}
                                            >
                                                {(() => {
                                                    const interval = c.billingIntervalMonths || 1;
                                                    const monthlyBudget = c.budgeted / interval;
                                                    return `Every ${interval} mo · $${monthlyBudget.toFixed(2)}/mo`;
                                                })()}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </TableCell>

                        {/* 3-Month Avg (immediately after category) */}
                        <TableCell align="right">
                            ${totals.average3Months.toFixed(2)}
                        </TableCell>

                        {/* Budgeted */}
                        <TableCell align="right" sx={{ width: 120 }}>
                            {hasSubs ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ minWidth: 100, display: "inline-block", textAlign: "right" }}
                                >
                                    ${totals.budgeted.toFixed(2)}
                                </Typography>
                            ) : (
                                <TextField
                                    variant="standard"
                                    type="number"
                                    size="small"
                                    inputProps={{
                                        min: 0,
                                        style: { textAlign: "right", minWidth: 100 },
                                    }}
                                    value={c.budgeted}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                        onChange(handleBudgetChange(c.id, Number(e.target.value)), { change: "budget" })
                                    }
                                />
                            )}
                        </TableCell>

                        {/* Actual */}
                        <TableCell align="right">${totals.actual.toFixed(2)}</TableCell>

                        {/* Remaining */}
                        <TableCell align="right">
                            ${(totals.budgeted - totals.actual).toFixed(2)}
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right" sx={{ width: 80 }}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddClick(c.id);
                                }}
                            >
                                <Add fontSize="small" />
                            </IconButton>
                        </TableCell>
                    </TableRow>

                    {hasSubs && isExpanded && renderRows(c.subcategories!, depth + 1)}
                </React.Fragment>
            );
        });

    return (
        <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="flex-end" mb={1}>
                <Button variant="contained" size="small" onClick={() => handleAddClick(null)}>
                    Add Category
                </Button>
            </Box>

            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">3-Month Avg</TableCell>
                        <TableCell align="right">Budgeted</TableCell>
                        <TableCell align="right">Actual</TableCell>
                        <TableCell align="right">Remaining</TableCell>
                        {/* <TableCell align="right">Actions</TableCell> */}
                    </TableRow>
                </TableHead>
                <TableBody>{renderRows(categories)}</TableBody>
            </Table>

            {/* Add Category Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>
                    {parentId ? "Add Subcategory" : "Add Category"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Name"
                        fullWidth
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                    />
                    {!parentId && (
                        <TextField
                            select
                            margin="dense"
                            label="Type"
                            fullWidth
                            value={newKind}
                            onChange={(e) =>
                                setNewKind(e.target.value as "income" | "expense")
                            }
                        >
                            <MenuItem value="income">Income</MenuItem>
                            <MenuItem value="expense">Expense</MenuItem>
                        </TextField>
                    )}
                    <Box mt={1} display="flex" gap={1} alignItems="center">
                        <TextField
                            margin="dense"
                            label="Every"
                            type="number"
                            value={newIntervalValue}
                            onChange={(e) =>
                                setNewIntervalValue(
                                    Math.max(1, Number(e.target.value) || 1)
                                )
                            }
                            sx={{ maxWidth: 120 }}
                        />
                        <TextField
                            select
                            margin="dense"
                            label="Unit"
                            value={newIntervalUnit}
                            onChange={(e) =>
                                setNewIntervalUnit(
                                    (e.target.value as "months" | "years") || "months"
                                )
                            }
                            sx={{ minWidth: 120 }}
                        >
                            <MenuItem value="months">months</MenuItem>
                            <MenuItem value="years">years</MenuItem>
                        </TextField>
                    </Box>
                    <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                        <Typography variant="caption" color="text.secondary">
                            Presets:
                        </Typography>
                        {[1, 3, 6, 12, 24].map((m) => (
                            <Button
                                key={m}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    setNewIntervalUnit("months");
                                    setNewIntervalValue(m);
                                }}
                            >
                                {m === 1 ? "Monthly" : `${m} mo`}
                            </Button>
                        ))}
                    </Box>

                    {createCategoryError && (
                        <Box mt={1}>
                            <Typography variant="body2" color="error">
                                {createCategoryError}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={creatingCategory}>Cancel</Button>
                    <Button onClick={handleAddConfirm} variant="contained" disabled={creatingCategory || !newName.trim()}>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Category Dialog */}
            <Dialog open={!!editTargetId} onClose={() => setEditTargetId(null)}>
                <DialogTitle>
                    {(() => {
                        const target = editTargetId
                            ? findCategoryById(categories, editTargetId)
                            : undefined;
                        return target?.name ?? "Edit category";
                    })()}
                </DialogTitle>
                <DialogContent>
                    {(() => {
                        const target = editTargetId
                            ? findCategoryById(categories, editTargetId)
                            : undefined;
                        if (!target) return null;

                        const isTopLevel = categories.some(c => c.id === target.id);
                        const hasChildren = !!target.subcategories?.length;
                        const isLeaf = !hasChildren;

                        return (
                            <>
                                {isTopLevel && (
                                    <TextField
                                        select
                                        margin="dense"
                                        label="Type"
                                        fullWidth
                                        value={editKind}
                                        onChange={(e) =>
                                            setEditKind(e.target.value as "income" | "expense")
                                        }
                                    >
                                        <MenuItem value="income">Income</MenuItem>
                                        <MenuItem value="expense">Expense</MenuItem>
                                    </TextField>
                                )}
                                {isLeaf && (
                                    <>
                                        <Box mt={1} display="flex" gap={1} alignItems="center">
                                            <TextField
                                                margin="dense"
                                                label="Every"
                                                type="number"
                                                value={editIntervalValue}
                                                onChange={(e) =>
                                                    setEditIntervalValue(
                                                        Math.max(1, Number(e.target.value) || 1)
                                                    )
                                                }
                                                sx={{ maxWidth: 120 }}
                                            />
                                            <TextField
                                                select
                                                margin="dense"
                                                label="Unit"
                                                value={editIntervalUnit}
                                                onChange={(e) =>
                                                    setEditIntervalUnit(
                                                        (e.target.value as "months" | "years") || "months"
                                                    )
                                                }
                                                sx={{ minWidth: 120 }}
                                            >
                                                <MenuItem value="months">months</MenuItem>
                                                <MenuItem value="years">years</MenuItem>
                                            </TextField>
                                        </Box>
                                        <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                Presets:
                                            </Typography>
                                            {[1, 3, 6, 12, 24].map((m) => (
                                                <Button
                                                    key={m}
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setEditIntervalUnit("months");
                                                        setEditIntervalValue(m);
                                                    }}
                                                >
                                                    {m === 1 ? "Monthly" : `${m} mo`}
                                                </Button>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleEditDelete}>
                        Delete
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={() => setEditTargetId(null)}>Cancel</Button>
                    <Button onClick={handleEditConfirm} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirm dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setDeleteConfirmTargetId(null);
                }}
            >
                <DialogTitle>Delete category?</DialogTitle>
                <DialogContent>
                    {usageCounts === null ? (
                        <Typography variant="body2">Checking references…</Typography>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Delete{' '}
                                <strong>
                                    {deleteConfirmTargetId
                                        ? (findCategoryById(categories, deleteConfirmTargetId)?.name ?? "this category")
                                        : "this category"}
                                </strong>
                                {' '}and all of its subcategories? This can’t be undone.
                            </Typography>

                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle2">What will happen</Typography>
                                <Typography variant="body2">• Transactions referencing this category: {usageCounts.transactions}</Typography>
                                <Typography variant="body2">• Categorization rules referencing this category: {usageCounts.rules}</Typography>
                                <Typography variant="body2">• Budget allocations referencing this category: {usageCounts.allocations}</Typography>
                                <Typography variant="body2">• Direct subcategories: {usageCounts.childCategories}</Typography>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={optUnassignTransactions}
                                            onChange={(e) => setOptUnassignTransactions(e.target.checked)}
                                        />
                                    }
                                    label="Set transactions to Unassigned"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={optDeleteRules}
                                            onChange={(e) => setOptDeleteRules(e.target.checked)}
                                        />
                                    }
                                    label="Delete matching categorization rules"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={optDeleteAllocations}
                                            onChange={(e) => setOptDeleteAllocations(e.target.checked)}
                                        />
                                    }
                                    label="Delete budget allocations for this category"
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            setDeleteConfirmTargetId(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}
